import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import MessagesSettings from './MessagesSettings';
import SelectReceivers from './SelectReceivers';
import vector from '../../../assets/icons/message-manager/vector.svg';
import NotificationModal from '../../../components/MessageManager/NotificationModal';
import successful from '../../../assets/icons/common/successful.svg';
import warn from '../../../assets/icons/message-manager/warn.svg';
import InputValidation from '../../../utils/InputValidation';
import API from '../../../utils/API';
import constants from '../../../utils/constants';
import Toast from '../../../components/Toast/Toast';

function getMaxLines(messageStyle) {
    switch (messageStyle) {
        case 'full-screen':
            return 12;
        case 'half-screen':
            return 6;
        case 'ticker':
            return 1;
        default:
            return 1;
    }
}

function CreateMessage(props) {
    const navigate = useNavigate();
    const { user } = props;
    const permissionIds = user.permissions.map(permission => permission.id);

    if (!permissionIds.includes(constants.permissionIds.msgManagerFrontDeskView)) {
        navigate('/forbidden');
    }
    const location = useLocation();
    const { id } = location.state || {};

    const [formData, setFormData] = useState({
        id: "",
        deliverMessage: 'now',
        messageStyle: 'ticker',
        uploadedImage: null,
        messageType: 'text',
        messageText: '',
        duration: 1,
        rotation: 1,
        title: '',
        date: null,
        time: null,
        recurringDays: [false, false, false, false, false, false, false],
        receivers: [],
        status: 'normal',
        messageTextLines: 1,
    });

    const [showSelectReceivers, setShowSelectReceivers] = useState(false);
    const [notificationProps, setNotificationProps] = useState({
        icon: successful,
        message: 'MESSAGE_SUCCESS',
        description: 'MESSAGE_SUCCESS_DESCRIPTION',
        primaryButtonText: 'CONTINUE',
        className: 'green',
    });
    const [validated, setValidated] = useState('');
    const [titleValid, setTitleValid] = useState('');
    const [messageTextValid, setMessageTextValid] = useState('');
    const [dateValid, setDateValid] = useState('');
    const [timeValid, setTimeValid] = useState('');
    const [recurringValid, setRecurringValid] = useState('');
    const [treeData, setTreeData] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState('');
    const modalRef = useRef(null);

    useEffect(() => {
        modalRef.current = new window.bootstrap.Modal(document.getElementById('notificationModal'));
        const fetchTreeData = async () => {
            const res = await API.getGroupsTree();
            const tree = [];
            const processGroupItem = (item) => {
                const model = { label: item.name, value: item.id };
                if (item.receivers) {
                    model.children = item.receivers.map((receiver) => {
                        return { label: receiver.name, value: item.id + '+' + receiver.id };
                    });
                }
                return model;
            };

            res.data.forEach((item) => {
                if (item.parentId === null) {
                    tree.push(processGroupItem(item));
                } else {
                    const parent = tree.find((model) => model.value === item.parentId);
                    if (parent) {
                        if (!parent.children) {
                            parent.children = [];
                        }
                        parent.children.push(processGroupItem(item));
                    }
                }
            });
            setTreeData([{ value: '-1', label: 'Select All Receivers', type: '', children: tree }]);
        };
        fetchTreeData();
    }, []);
    
    useEffect(() => {
        return () => {
            if (id) {
                const createTab = document.getElementById('messageManagerCreateMessageTab');
                if (createTab) {
                    createTab.classList.remove('active');
                }
            }
        }
    }, []);

    const validateMessageText = (messageText, messageTextLines) => {
        const tempElement = document.createElement('div');
        tempElement.innerHTML = messageText;
        const textContent = tempElement.textContent || tempElement.innerText || '';
        let isMessageTextValid = InputValidation.validateEmpty(textContent);
        
        if (!isMessageTextValid) {
            let pTags = document.getElementsByClassName("ql-editor")[0].querySelectorAll('p');
            let totalLines = 0;
            pTags.forEach(p => {
                const height = p.offsetHeight;
                totalLines += Math.ceil(height / 23);
            });
            isMessageTextValid = totalLines > messageTextLines ? 'LIMIT_LINES' : '';
        }
        return isMessageTextValid;
    };

    useEffect(() => {
        setFormData((prevFormData) => {
            const maxLines = getMaxLines(prevFormData.messageStyle);
            return {
                ...prevFormData,
                messageType: 'text',
                messageTextLines: maxLines,
            };
        });
    }, [formData.messageStyle]);

    useEffect(() => {
        let isMessageTextValid = validateMessageText(formData.messageText, formData.messageTextLines);
        setMessageTextValid(isMessageTextValid);
    }, [formData.messageTextLines]);

    useEffect(() => {
        setTitleValid(InputValidation.validateEmpty(formData.title));
    }, [formData.title]);

    useEffect(() => {
        if (formData.messageType === 'text') {
            if (isInitialized && formData.messageText) {
                setIsInitialized(false);
            }
            let isMessageTextValid = validateMessageText(formData.messageText, formData.messageTextLines);
            setMessageTextValid(isMessageTextValid);
        }
    }, [formData.messageText, formData.messageType, isInitialized]);

    useEffect(() => {
        if (formData.deliverMessage === 'once') {
            setDateValid(formData.date ? '' : 'EMPTY_FIELD_ERROR');
            setTimeValid(formData.time ? '' : 'EMPTY_FIELD_ERROR');
        }else if (formData.deliverMessage === 'recurring') {
            setTimeValid(formData.time ? '' : 'EMPTY_FIELD_ERROR');
        } else {
            setDateValid("");
            setTimeValid("");
        }
    }, [formData.date, formData.time, formData.deliverMessage]);

    useEffect(() => {
        if (formData.deliverMessage === 'recurring') {
            setRecurringValid(formData.recurringDays.some(day => day) ? '' : 'RECURRING_TIME_ERROR');
        } else {
            setRecurringValid('');
        }
    }, [formData.recurringDays, formData.deliverMessage]);

    useEffect(() => {
        if (id) {
            const fetchMessageData = async () => {
                try {
                    const res = await API.getMessagesId(id);
                    const messageData = res.data;
                    setFormData({
                        id: messageData.id,
                        deliverMessage: messageData.scheduler.type,
                        messageStyle: messageData.format,
                        uploadedImage: messageData.media,
                        messageType: messageData.type,
                        messageText: messageData.message,
                        duration: messageData.scheduler.duration,
                        rotation: messageData.scheduler.repetitions,
                        title: messageData.title,
                        recurringDays: messageData.scheduler.recurrenceDays || [false, false, false, false, false, false, false],
                        receivers: messageData.receivers.map(receiver => `${receiver.groupId}+${receiver.mac}`),
                        status: messageData.status,
                        messageTextLines: getMaxLines(messageData.format),
                    });
                    if(messageData.scheduler.type === 'recurring') {
                        setFormData((prevFormData) => ({
                            ...prevFormData,
                            recurringDays: messageData.scheduler.recurrenceDays,
                            date: null,
                            time: new Date(messageData.scheduler.timeStart),
                        }));
                    }else if (messageData.scheduler.type === 'once') {
                        setFormData((prevFormData) => ({
                            ...prevFormData,
                            date:new Date(messageData.scheduler.timeStart),
                            time: new Date(messageData.scheduler.timeStart),
                        }));
                    }else {
                        setFormData((prevFormData) => ({
                            ...prevFormData,
                            date: null,
                            time: null,
                        }));
                    }
                } catch (err) {
                    console.error('Failed to fetch message data:', err);
                }
            };
            fetchMessageData();
            setIsInitialized(true);
            if (document.getElementById('messageManagerCreateMessageTab')) {
                document.getElementById('messageManagerCreateMessageTab').classList.add('active');
            }
            if (document.getElementById('messageManagerStatusTab')) {
                document.getElementById('messageManagerStatusTab').classList.remove('active');
            }
        }
    }, [id]);

    function handleImageUpload(event) {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setFormData({ ...formData, uploadedImage: e.target.result });
            };
            reader.readAsDataURL(file);
        }
    }

    function handleImageRemove() {
        setFormData({ ...formData, uploadedImage: null });
    }

    const isTimeInPast = (date, time) => {
        const selectedDateTime = new Date(date);
        selectedDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);
        const now = new Date();
        now.setSeconds(0, 0);
        return selectedDateTime < now;
    };

    const isDateInPast = (date) => {
        const selectedDate = new Date(date);
        selectedDate.setHours(0, 0, 0, 0);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return selectedDate < now;
    };

    const handleSelectReceiversClick = () => {
        setValidated(true);

        const isTitleValid = InputValidation.validateEmpty(formData.title);
        let isMessageTextValid = formData.messageType === 'text' ? validateMessageText(formData.messageText, formData.messageTextLines) : '';
        let isDateValid = formData.deliverMessage === 'once' ? (formData.date ? '' : 'EMPTY_FIELD_ERROR') : '';
        let isTimeValid = (formData.deliverMessage === 'once' || formData.deliverMessage === 'recurring') ? (formData.time ? '' : 'EMPTY_FIELD_ERROR') : '';
        
        if(!isDateValid && formData.deliverMessage === 'once' && isDateInPast(formData.date)){
            isDateValid = 'START_DATE_ERROR';
        } else if (!isDateValid && !isTimeValid && formData.deliverMessage === 'once' && isTimeInPast(formData.date, formData.time)) {
            isTimeValid = 'START_TIME_ERROR';
        }
        const isRecurringDaysValid = (formData.deliverMessage === 'recurring' && !formData.recurringDays.some(day => day)) ? 'RECURRING_TIME_ERROR' : '';
        
        setRecurringValid(isRecurringDaysValid);
        setTitleValid(isTitleValid);
        setMessageTextValid(isMessageTextValid);
        setDateValid(isDateValid);
        setTimeValid(isTimeValid);

        const isFormValid = !isRecurringDaysValid && !isTitleValid && !isDateValid && !isTimeValid && ((formData.messageType === 'text' && !isMessageTextValid) || formData.messageType === 'image');
        setShowSelectReceivers(isFormValid);
    };

    const handleReceiversChange = (checked) => {
        setFormData({ ...formData, receivers: checked });
    }

    const handleBackClick = () => {
        setShowSelectReceivers(false);
        setIsInitialized(true);
    }

    const processedReceivers = formData.receivers.map(receiver => {
        const [groupId, mac] = receiver.split('+');
        return { groupId, mac };
    });

    const getNextDate = () => {
        const { recurringDays, time } = formData;
        const now = new Date();
        const currentDay = now.getDay();
    
        // Helper function to add days to a date
        const addDays = (date, days) => {
            const result = new Date(date);
            result.setDate(result.getDate() + days);
            return result;
        };
    
        // Helper function to combine date and time, setting seconds and milliseconds to zero
        const combineDateAndTime = (date, time) => {
            const combined = new Date(date);
            combined.setHours(time.getHours(), time.getMinutes(), 0, 0); // 设置秒和毫秒为零
            return combined;
        };
    
        // Set current time's seconds and milliseconds to zero for comparison
        now.setSeconds(0, 0);
    
        // Find the next recurring day
        for (let i = 0; i < 7; i++) {
            const dayIndex = (currentDay + i) % 7;
            if (recurringDays[dayIndex]) {
                const nextDate = addDays(now, i);
                const nextDateTime = combineDateAndTime(nextDate, time);
    
                if (nextDateTime > now) {
                    return nextDateTime;
                }
            }
        }
    
        // If no valid recurring day is found, return the next occurrence of the first true day
        for (let i = 1; i <= 7; i++) {
            const dayIndex = (currentDay + i) % 7;
            if (recurringDays[dayIndex]) {
                const nextDate = addDays(now, i);
                return combineDateAndTime(nextDate, time);
            }
        }
    
        // Default to the next occurrence of the first true day
        return combineDateAndTime(addDays(now, 7), time);
    };

    function toIsoStringWithOffset(date) {
    var tzo = -date.getTimezoneOffset(),
        dif = tzo >= 0 ? '+' : '-',
        pad = function(num) {
            return (num < 10 ? '0' : '') + num;
        };
    
    return date.getFullYear() +
        '-' + pad(date.getMonth() + 1) +
        '-' + pad(date.getDate()) +
        'T' + pad(date.getHours()) +
        ':' + pad(date.getMinutes()) +
        ':' + pad(date.getSeconds()) +
        dif + pad(Math.floor(Math.abs(tzo) / 60)) +
        ':' + pad(Math.abs(tzo) % 60);
    }

    const validateDateTime = (date, time) => {
        if (isDateInPast(date)) {
            setError(<FormattedMessage id="START_DATE_ERROR" />);
            setDateValid('START_DATE_ERROR');
            return false;
        } else if (isTimeInPast(date, time)) {
            setError(<FormattedMessage id="START_TIME_ERROR" />);
            setTimeValid('START_TIME_ERROR');
            return false;
        }
        return true;
    };

    const handleSendMessagesClick = async () => {
        if (formData.receivers && formData.receivers.length === 0) {
            setError(<FormattedMessage id="SELECT_RECEIVER_WARNING" />);
            return;
        }
        const date = formData.date || new Date();
        const time = formData.time || new Date();
        let now = new Date();
        now.setSeconds(0);
        now.setMilliseconds(0); 

        let timeStart = null;
        if (formData.deliverMessage === 'recurring') {
            const recurringDate = getNextDate();
            let timeStartDate = new Date(
                recurringDate.getFullYear(),
                recurringDate.getMonth(),
                recurringDate.getDate(),
                time.getHours(),
                time.getMinutes(),
                0,0
            )
            timeStart = toIsoStringWithOffset(timeStartDate)
        } else if (formData.deliverMessage === 'once') {
            if (!validateDateTime(formData.date, formData.time))
                return;
            timeStart = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                time.getHours(),
                time.getMinutes(),
                0,0
            )
            timeStart = timeStart.toISOString();
        } else {
            timeStart = now.toISOString();
        }
        const messageData = {
            ...formData,
            timeStart: timeStart,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            recurringDays:formData.deliverMessage !== 'recurring' ? null : formData.recurringDays,
            receivers: processedReceivers,
        };
        try {
            if (formData.id) {
                await API.updateMessage({ ...formData.id, ...messageData });
            } else {
                await API.createMessage(messageData);
            }
            setNotificationProps({
                icon: successful,
                message: 'MESSAGE_SUCCESS',
                description: 'MESSAGE_SUCCESS_DESCRIPTION',
                primaryButtonText: 'CONTINUE',
                className: 'green',
                onSubmit: () => {
                    modalRef.current.hide();
                    if (id) { 
                        navigate('/message-manager/status');
                        document.getElementById('messageManagerCreateMessageTab').classList.remove('active');
                        document.getElementById('messageManagerStatusTab').classList.add('active');
                    }else 
                        document.getElementById('messageManagerStatusTab').click();
                }
            });
        } catch (err) {
            setNotificationProps({
                icon: warn,
                message: 'MESSAGE_FAILED',
                description: 'MESSAGE_FAILED_DESCRIPTION',
                primaryButtonText: 'CONTINUE',
                className: 'red',
                onSubmit: () => {
                    modalRef.current.hide();
                }
            });
        }
        modalRef.current.show();
    };

    const selectReceiverss = () => {
        return (
            <>
                <SelectReceivers
                    treeData={treeData}
                    checked={formData.receivers}
                    onCheck={handleReceiversChange}
                    onBack={handleBackClick}
                />
                <div className="col-12 " style={{ height: '160px' }}></div>
                <div className="fixed-bottom-container">
                    <button type="button" className="btn btn-primary but-darkblue  me-3 create-mes-button" onClick={handleSendMessagesClick}>
                        <img src={vector} alt="Send Icon" className="me-2" />
                        <FormattedMessage id="SEND_MESSAGE" />
                    </button>
                </div>
            </>
        );
    };

    const messagesSettingss = () => {
        return (
            <>
                <MessagesSettings
                    formData={formData}
                    setFormData={setFormData}
                    handleImageUpload={handleImageUpload}
                    handleImageRemove={handleImageRemove}
                    validated={validated}
                    titleValid={titleValid}
                    messageTitle={formData.id ? "EDIT_YOUR_MESSAGE" : "CREATE_MESSAGE"}
                    messageTextValid={messageTextValid}
                    dateValid={dateValid}
                    timeValid={timeValid}
                    isInitialized={isInitialized}
                    recurringValid={recurringValid}
                />
                <div className="col-12 " style={{ height: '160px' }}></div>
                <div className="fixed-bottom-container">
                    <button type="button" className="btn btn-primary but-darkblue me-3 create-mes-button" onClick={handleSelectReceiversClick}>
                        <FormattedMessage id="SELECT_RECEIVERS" />
                    </button>
                </div>
            </>
        );
    };

    return (
        <>
            <NotificationModal {...notificationProps} />
            {showSelectReceivers ? selectReceiverss() : messagesSettingss()}
            <Toast
                error={error}
                setError={setError}
                className={"toast-message"}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    user: state.user,
});

export default connect(mapStateToProps)(CreateMessage);
